import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ExerciseEditor from './ExerciseEditor';
import Button from '../common/Button';
import { ASSET_PATHS } from '../../utils/gameConfig';
import { updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface ExerciseData {
  day: number;
  mediaType: 0 | 1 | 2 | 3 | 4 | 5;
  title: string;
  instructions: string;
  mediaUrl: string;
}

interface ExerciseSetupProps {
  forestId: string;
  forestName: string;
  gameDuration: number;
  onComplete: (exercises: ExerciseData[]) => void;
  onBack: () => void;
}

const ExerciseSetup: React.FC<ExerciseSetupProps> = ({
  forestId,
  forestName,
  gameDuration,
  onComplete,
  onBack
}) => {
  const [exercises, setExercises] = useState<ExerciseData[]>([]);
  const [openDay, setOpenDay] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  //Initialize exercises
  useEffect(() => {
    const initialExercises: ExerciseData[] = [];
    for (let day = 1; day <= gameDuration; day++) {
      initialExercises.push({
        day,
        mediaType: 2, //Default to "Written instructions only"
        title: '',
        instructions: '',
        mediaUrl: ''
      });
    }
    setExercises(initialExercises);
  }, [gameDuration]);


  const handleSaveAsDraft = async () => {
    if (!forestId) return;
    
    try {
      //Draft/inactive
      await updateDoc(doc(db, 'forests', forestId), {
        isDraft: true,
        isActive: false,
        updatedAt: serverTimestamp(),
      });
      
      console.log('Forest saved as draft:', forestId);
      
      //Kick back to teacher dashboard
      onBack();
    } catch (error) {
      console.error('Error saving forest as draft:', error);
      onBack();
    }
  };


  const handleExerciseChange = (day: number, exerciseData: ExerciseData) => {
    setExercises(prev => 
      prev.map(ex => ex.day === day ? exerciseData : ex)
    );
  };

  const isExerciseComplete = (exercise: ExerciseData) => {
    //Sanity check
    if (!exercise.title.trim() || !exercise.instructions.trim()) {
      return false;
    }
    
    //Is URL required?
    const requiresUrl = [1, 3, 4, 5].includes(exercise.mediaType);
    if (requiresUrl && !exercise.mediaUrl.trim()) {
      return false;
    }
    
    return true;
  };

  const getCompletionStats = () => {
    const completed = exercises.filter(isExerciseComplete).length;
    const total = exercises.length;
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  const canSubmit = () => {
    //Ensure all are complete
    if (exercises.length === 0) {
      return false;
    }
    
    //Verify completeness
    const allComplete = exercises.every(exercise => isExerciseComplete(exercise));
    
    console.log('Exercise validation:', {
      totalExercises: exercises.length,
      completedExercises: exercises.filter(isExerciseComplete).length,
      allComplete,
      exercises: exercises.map(ex => ({
        day: ex.day,
        title: ex.title.trim(),
        instructions: ex.instructions.trim(),
        mediaType: ex.mediaType,
        mediaUrl: ex.mediaUrl.trim(),
        isComplete: isExerciseComplete(ex)
      }))
    });
    
    return allComplete;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    
    setIsSubmitting(true);
    try {
      await onComplete(exercises);
    } catch (error) {
      console.error('Error saving exercises:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const { completed, total, percentage } = getCompletionStats();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="w-[95%] max-w-5xl h-[95vh] bg-white rounded-2xl shadow-2xl border border-powder-200 overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="p-6 border-b border-powder-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-powder-800">
                Setup Daily Exercises
              </h1>
              <p className="text-powder-600 mt-1">
                Configure exercises for "{forestName}" ({gameDuration} days)
              </p>
            </div>
            
            <Button
              variant="outline"
              onClick={onBack}
              disabled={isSubmitting}
            >
              ← Back to Forest Setup
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-powder-600">Progress</span>
              <span className={`font-medium ${completed === total ? 'text-green-600' : 'text-powder-800'}`}>
                {completed}/{total} exercises complete ({percentage}%)
              </span>
            </div>
            <div className="w-full bg-powder-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  completed === total ? 'bg-green-500' : 'bg-sage-500'
                }`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Exercise List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-2">
            {exercises.map((exercise) => (
              <ExerciseEditor
                key={exercise.day}
                day={exercise.day}
                exercise={exercise}
                onChange={handleExerciseChange}
                isOpen={openDay === exercise.day}
                onToggle={() => setOpenDay(openDay === exercise.day ? 0 : exercise.day)}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-powder-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="text-sm text-powder-600">
              {!canSubmit() && (
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>Complete all exercises before finishing setup</span>
                </div>
              )}
              {canSubmit() && (
                <div className="flex items-center space-x-2 text-green-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>All exercises complete! Ready to create forest.</span>
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleSaveAsDraft}
                disabled={isSubmitting}
              >
                Save as Draft
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!canSubmit() || isSubmitting}
              >
                {isSubmitting ? 'Creating Forest...' : 'Complete Forest Setup'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseSetup;